import {
  DataGridPremium as DataGrid,
  GridColDef,
  GridSortModel,
  GridCallbackDetails,
  GridToolbarContainer,
  GridRowParams,
  GridToolbarExport,
  useGridApiRef,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid-premium';
import { Box, CircularProgress, Paper, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ReactNode } from 'react';
import { Colors } from '../styles';

const ToolbarColumnsButton =
  GridToolbarColumnsButton as unknown as React.ComponentType;
const ToolbarFilterButton =
  GridToolbarFilterButton as unknown as React.ComponentType;
const ToolbarDensitySelector =
  GridToolbarDensitySelector as unknown as React.ComponentType;

export type DataGridOptions = {
  loading: boolean;
  rowsPerPageOptions?: number[];
  totalRows: number;
};

export type DataGridProps<T> = {
  columns: GridColDef[];
  rows: T[];
  page: number;
  pageSize: number;
  dataGridOptions: DataGridOptions;
  checkboxSelection: boolean;
  showPageSizeAll: boolean;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortModelChange: (
    model: GridSortModel,
    details: GridCallbackDetails,
  ) => void | undefined;
  disableSelectionOnClick?: boolean;
  onRowClick?: (params: GridRowParams) => void;
  exportName?: string;
  columnVisibilityModel?: Record<string, boolean>;
  toolbarContent?: ReactNode;
};

/* function CustomMobileToolbar() {
  return (
    <GridToolbarContainer>
      // <GridToolbarColumnsButton />
      // <GridToolbarFilterButton /> 
    </GridToolbarContainer>
  );
} */

function CustomToolbar(props: any) {
  const { fileName, toolbarContent } = props;
  return (
    <GridToolbarContainer
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Standardfunktioner */}
        <ToolbarColumnsButton />
        <ToolbarFilterButton />
        <ToolbarDensitySelector />
        {/* Exportfunktioner */}
        <GridToolbarExport
          csvOptions={{
            allColumns: true, // Exportera alla kolumner
            fileName: fileName || 'instalco_export', // Anpassa filnamnet
          }}
          excelOptions={{
            allColumns: true, // Exportera alla kolumner
            includeHeaders: true, // Inkludera kolumnrubriker
            includeHiddenColumns: true, // Inkludera dolda kolumner
            fileName: fileName || 'instalco_export', // Anpassa filnamnet
          }}
        />
        {toolbarContent ? (
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            {toolbarContent}
          </Box>
        ) : null}
      </Box>
    </GridToolbarContainer>
  );
}

function CustomLoadingOverlay() {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        backgroundColor: Colors.WHITE,
      }}
    >
      <CircularProgress />
    </Box>
  );
}

export const DataGridPremium = <T,>(props: DataGridProps<T>) => {
  const {
    columns,
    rows,
    page,
    pageSize,
    dataGridOptions: { loading, totalRows },
    checkboxSelection,
    onPageChange,
    onPageSizeChange,
    showPageSizeAll,
    onSortModelChange,
    disableSelectionOnClick,
    onRowClick,
    exportName,
    columnVisibilityModel,
    toolbarContent,
  } = props;

  const apiRef = useGridApiRef();

  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const rowsPerPageOptions = [25, 50, 100, 300, 500];

  if (totalRows > 500 && showPageSizeAll) {
    rowsPerPageOptions.push(totalRows);
  }

  return (
    <Paper
      sx={{
        height: isMobile ? '80vh' : '60vh',
        boxShadow: 'none',
        overflow: 'auto',
        '.MuiDataGrid-root': { border: 'none', fontSize: 12 },
        '.MuiDataGrid-footerContainer': {
          borderTop: '1px solid lightgray',
        },
        '.MuiDataGrid-overlay': {
          backgroundColor: Colors.WHITE,
          fontSize: 16,
        },
      }}
    >
      <DataGrid
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        density='standard'
        rowCount={totalRows}
        rowsPerPageOptions={rowsPerPageOptions}
        checkboxSelection={checkboxSelection}
        paginationMode='server'
        page={page}
        pageSize={pageSize}
        loading={loading}
        onPageChange={(newPage) => onPageChange(newPage)}
        onPageSizeChange={(newPageSize) => onPageSizeChange(newPageSize)}
        onSortModelChange={onSortModelChange}
        filterMode='server'
        sortingMode='server'
        disableColumnFilter
        pagination
        disableSelectionOnClick={disableSelectionOnClick}
        onRowClick={onRowClick}
        columnVisibilityModel={columnVisibilityModel}
        localeText={{
          columnsPanelTextFieldPlaceholder: t('datagrid.columnsPanelTextField'),
          columnsPanelTextFieldLabel: t('datagrid.columnsPanelTextFieldLabel'),
          columnsPanelShowAllButton: t('datagrid.columnsPanelShowAllButton'),
          columnsPanelHideAllButton: t('datagrid.columnsPanelHideAllButton'),
          toolbarColumns: t('datagrid.toolbarColumns'),
          toolbarDensity: t('datagrid.toolbarDensity'),
          toolbarDensityLabel: t('datagrid.toolbarDensityLabel'),
          toolbarDensityCompact: t('datagrid.toolbarDensityCompact'),
          toolbarDensityStandard: t('datagrid.toolbarDensityStandard'),
          toolbarDensityComfortable: t('datagrid.toolbarDensityComfortable'),
          toolbarExport: t('datagrid.toolbarExport'),
          toolbarExportPrint: t('datagrid.toolbarExportPrint'),
          toolbarExportExcel: t('datagrid.toolbarExportExcel'),
          toolbarExportCSV: t('datagrid.toolbarExportCSV'),
          toolbarFilters: t('datagrid.toolbarFilters'),
          columnMenuSortAsc: t('datagrid.columnMenuSortAsc'),
          columnMenuSortDesc: t('datagrid.columnMenuSortDesc'),
          columnMenuHideColumn: t('datagrid.columnMenuHideColumn'),
          columnMenuShowColumns: t('datagrid.columnMenuShowColumns'),
          columnMenuLabel: t('datagrid.columnMenuLabel'),
          columnMenuFilter: t('datagrid.columnMenuFilter'),
          columnMenuUnsort: t('datagrid.columnMenuUnsort'),
          noRowsLabel: t('datagrid.noRowsLabel'),
        }}
        components={{
          LoadingOverlay: CustomLoadingOverlay,
          Toolbar: CustomToolbar,
        }}
        componentsProps={{
          toolbar: { fileName: exportName, toolbarContent }, // Skickar exportName som prop
        }}
        sx={{
          // '& .MuiDataGrid-row:hover': {
          //   cursor: 'pointer',
          // },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'inherit',
          },
        }}
      />
    </Paper>
  );
};

DataGridPremium.defaultProps = {
  disableSelectionOnClick: false,
  onRowClick: undefined,
  showPageSizeAll: true,
};
